import { useState, useEffect, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Archive,
  ArchiveRestore,
  Loader2,
  MoreHorizontal,
  Copy,
  Trash2,
  Package,
  Ticket,
  Video,
  Music,
} from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProductTypeEnum } from "@/types/ProductType";
import { MeetingFormatEnum } from "@/types/MeetingFormat";
import { cn } from "@/lib/utils";

type CreateProductType = "standard" | ProductTypeEnum.TICKET | ProductTypeEnum.MEETING | ProductTypeEnum.TRACK;

const createProductTypeOptions: {
  value: CreateProductType;
  label: string;
  icon: ReactNode;
}[] = [
  { value: "standard", label: "Standard", icon: <Package className="h-6 w-6" /> },
  { value: ProductTypeEnum.TICKET, label: "Ticket", icon: <Ticket className="h-6 w-6" /> },
  { value: ProductTypeEnum.MEETING, label: "Meeting", icon: <Video className="h-6 w-6" /> },
  { value: ProductTypeEnum.TRACK, label: "Track", icon: <Music className="h-6 w-6" /> },
];

interface Product {
  _id: string;
  name?: string;
  price?: number;
  archivedAt?: string | null;
}

type ProductTab = "active" | "archived";

export function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<ProductTab>("active");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [productName, setProductName] = useState("");
  const [productType, setProductType] = useState<CreateProductType | "">("");
  const [meetingFormat, setMeetingFormat] = useState<MeetingFormatEnum>(
    MeetingFormatEnum.WEBINAR,
  );
  const [cloningProductId, setCloningProductId] = useState<string | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null
  );
  const [archivingProductId, setArchivingProductId] = useState<string | null>(
    null
  );
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const refreshProducts = async () => {
    const refreshResponse = await fetch("/api/products");
    if (refreshResponse.ok) {
      const refreshData = await refreshResponse.json();
      setProducts(refreshData.products || []);
    }
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/products");

        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
        } else {
          console.error("Failed to fetch products");
        }

        setBreadcrumbs([
          { label: "Sales", path: "/products" },
          { label: "Products", path: "/products" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const visibleProducts = useMemo(() => {
    const filtered =
      activeTab === "archived"
        ? products.filter((product) => product.archivedAt)
        : products.filter((product) => !product.archivedAt);
    return [...filtered].sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }),
    );
  }, [activeTab, products]);

  const resetCreateForm = () => {
    setProductName("");
    setProductType("");
    setMeetingFormat(MeetingFormatEnum.WEBINAR);
  };

  const handleCreate = async () => {
    if (!productName.trim() || !productType) {
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch("/api/products", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: productName,
          ...(productType !== "standard" ? { type: productType } : {}),
          ...(productType === ProductTypeEnum.MEETING
            ? { meeting: { meetingFormat } }
            : {}),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const newId = data.product?._id as string | undefined;
        setIsCreateDialogOpen(false);
        resetCreateForm();
        await refreshProducts();
        if (newId) {
          navigate(`/products/${newId}`);
        }
      } else {
        const data = await response.json();
        console.error("Failed to create product:", data.error);
        alert(data.error || "Failed to create product");
      }
    } catch (error) {
      console.error("Failed to create product:", error);
      alert("An error occurred while creating the product");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloneProduct = async ({ productId }: { productId: string }) => {
    setCloningProductId(productId);
    try {
      const response = await fetch(`/api/products/${productId}/clone`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        await refreshProducts();
        const newId = data.product?._id as string | undefined;
        if (newId) {
          navigate(`/products/${newId}`);
        }
      } else {
        const err = await response.json();
        console.error("Failed to clone product:", err.error);
        alert(err.error || "Failed to clone product");
      }
    } catch (error) {
      console.error("Failed to clone product:", error);
      alert("An error occurred while cloning the product");
    } finally {
      setCloningProductId(null);
    }
  };

  const handleSetArchived = async ({
    productId,
    archived,
  }: {
    productId: string;
    archived: boolean;
  }) => {
    const product = products.find((p) => p._id === productId);
    const actionLabel = archived ? "archive" : "unarchive";
    const confirmed = window.confirm(
      `${archived ? "Archive" : "Unarchive"} product "${product?.name || "Product"}"?`
    );
    if (!confirmed) return;

    setArchivingProductId(productId);
    try {
      const response = await fetch(`/api/products/${productId}/archive`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ archived }),
      });

      if (response.ok) {
        await refreshProducts();
      } else {
        const err = await response.json();
        console.error(`Failed to ${actionLabel} product:`, err.error);
        alert(err.error || `Failed to ${actionLabel} product`);
      }
    } catch (error) {
      console.error(`Failed to ${actionLabel} product:`, error);
      alert(`An error occurred while trying to ${actionLabel} the product`);
    } finally {
      setArchivingProductId(null);
    }
  };

  const handleDeleteProduct = async ({ productId }: { productId: string }) => {
    const product = products.find((p) => p._id === productId);
    const confirmed = window.confirm(
      `Delete product "${product?.name || "Product"}"? It will be hidden from the store and admin lists.`
    );
    if (!confirmed) return;

    setDeletingProductId(productId);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        await refreshProducts();
      } else {
        const err = await response.json();
        console.error("Failed to delete product:", err.error);
        alert(err.error || "Failed to delete product");
      }
    } catch (error) {
      console.error("Failed to delete product:", error);
      alert("An error occurred while deleting the product");
    } finally {
      setDeletingProductId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Products</h1>
        <Button
          variant="default"
          size="sm"
          onClick={() => setIsCreateDialogOpen(true)}
          data-testid="products-create"
        >
          Create
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as ProductTab)}
        className="mb-4"
      >
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabsList>
      </Tabs>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="w-[72px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleProducts.length > 0 ? (
            visibleProducts.map((product) => (
              <TableRow
                key={product._id}
                className="hover:bg-muted/50"
              >
                <TableCell
                  className="cursor-pointer"
                  onClick={() => navigate(`/products/${product._id}`)}
                >
                  {product.name || "-"}
                </TableCell>
                <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Product actions"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          handleCloneProduct({ productId: product._id })
                        }
                        disabled={
                          cloningProductId === product._id ||
                          deletingProductId === product._id ||
                          archivingProductId === product._id
                        }
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {cloningProductId === product._id
                          ? "Cloning..."
                          : "Clone"}
                      </DropdownMenuItem>
                      {activeTab === "active" ? (
                        <DropdownMenuItem
                          onClick={() =>
                            handleSetArchived({
                              productId: product._id,
                              archived: true,
                            })
                          }
                          disabled={archivingProductId === product._id}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          {archivingProductId === product._id
                            ? "Archiving..."
                            : "Archive"}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() =>
                            handleSetArchived({
                              productId: product._id,
                              archived: false,
                            })
                          }
                          disabled={archivingProductId === product._id}
                        >
                          <ArchiveRestore className="h-4 w-4 mr-2" />
                          {archivingProductId === product._id
                            ? "Unarchiving..."
                            : "Unarchive"}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() =>
                          handleDeleteProduct({ productId: product._id })
                        }
                        disabled={deletingProductId === product._id}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {deletingProductId === product._id
                          ? "Deleting..."
                          : "Delete"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} className="h-24 text-center">
                {activeTab === "archived"
                  ? "No archived products."
                  : "No products found."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            resetCreateForm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Product</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">Name</Label>
              <Input
                id="product-name"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Enter product name"
                data-testid="products-create-name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && productName.trim() && productType) {
                    handleCreate();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Product type</Label>
              <div className="grid grid-cols-2 gap-3">
                {createProductTypeOptions.map((opt) => (
                  <div key={opt.value} className="aspect-square w-full min-w-0">
                    <Button
                      type="button"
                      variant="outline"
                      aria-pressed={productType === opt.value}
                      onClick={() => setProductType(opt.value)}
                      data-testid={`products-create-type-${opt.value}`}
                      className={cn(
                        "w-full h-full flex flex-col gap-2 p-4 bg-background",
                        productType === opt.value &&
                          "border-primary ring-2 ring-primary ring-offset-2",
                      )}
                    >
                      {opt.icon}
                      <span className="font-semibold text-sm">{opt.label}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {productType === ProductTypeEnum.MEETING ? (
              <div className="space-y-2">
                <Label htmlFor="meeting-format">Meeting format</Label>
                <Select
                  value={meetingFormat}
                  onValueChange={(value) =>
                    setMeetingFormat(value as MeetingFormatEnum)
                  }
                >
                  <SelectTrigger id="meeting-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MeetingFormatEnum.WEBINAR}>Webinar</SelectItem>
                    <SelectItem value={MeetingFormatEnum.LIVESTREAM}>
                      Livestream
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetCreateForm();
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!productName.trim() || !productType || isCreating}
              data-testid="products-create-submit"
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
